import BaseValidator from '../internals/validation';
import { forEach, forOwn, } from '../utils';
import { LogLevel, } from '..';
import { CartItemChildType, CartProduct, CartTicket, } from '.';
import { slugError } from '../internals/errors/errors';
export default class CartValidator extends BaseValidator {
    constructor(cart, logger) {
        super();
        this._cart = cart;
        this._log = logger;
    }
    /**
     *  Validate all booker cart metadata.
     *
     *  This will return true iff all metadata is valid.
     *  On failure, the errors are set on the cart metadata object
     *
     *  ```ts
     *  const validationResponse: ValidationResponse = cart.validator.bookerMetadata();
     *  ```
     *
     *  | Error                                    | Reason                              |
     *  | :--------------------------------------- | :---------------------------------- |
     *  | [ShopError](shoperror.html)              | No or invalid cartMetadata provided |
     *
     *  Note: Failed validation will NOT throw an error
     */
    bookerMetadata(mutateErrors) {
        return mergeValidation({
            booker: mergeValidation(Object.assign({}, this._cart.booker), this.metadata.bind(this), mutateErrors),
            metadata: mergeValidation(this._cart.metadata, this.metadata.bind(this), mutateErrors),
        }, (item) => item);
    }
    /**
     *  Validate a given ticket's cart metadata.
     *
     *  This will return true iff all metadata is valid.
     *  On failure, the errors are set on the cart metadata object
     *
     *  ```ts
     *  const validationResponse: ValidationResponse = cart.validator.ticketMetadata(cartTicket);
     *  ```
     *
     *  | Error                                    | Reason                              |
     *  | :--------------------------------------- | :---------------------------------- |
     *  | [ShopError](shoperror.html)              | No or invalid cartMetadata provided |
     *
     *  Note: Failed validation will NOT throw an error
     */
    ticketMetadata(cartTicket, mutateErrors) {
        if (!CartTicket.isCartTicket(cartTicket)) {
            throw slugError('Failed to validate ticket metadata -> invalid cart ticket', ['validation', 'ticketMetadata', 'error', 'ticket']);
        }
        return this._validateCartItemMetadata(cartTicket, mutateErrors);
    }
    /**
     *  Validate all ticket cart metadata.
     *
     *  This will return true iff all metadata is valid.
     *  On failure, the errors are set on the cart metadata object
     *
     *  ```ts
     *  const validationResponse: ValidationResponse = cart.validator.allTicketMetadata();
     *  ```
     *
     *  | Error                                    | Reason                              |
     *  | :--------------------------------------- | :---------------------------------- |
     *  | [ShopError](shoperror.html)              | No or invalid cartMetadata provided |
     *
     *  Note: Failed validation will NOT throw an error
     */
    allTicketMetadata(mutateErrors) {
        return mergeValidation(this._cart.flatItems[CartItemChildType.Tickets], this.ticketMetadata.bind(this), mutateErrors);
    }
    /**
     *  Validate a given product's cart metadata.
     *
     *  This will return true iff all metadata is valid.
     *  On failure, the errors are set on the cart metadata object
     *
     *  ```ts
     *  const validationResponse: ValidationResponse = cart.validator.productMetadata(cartProduct);
     *  ```
     *
     *  | Error                                    | Reason                              |
     *  | :--------------------------------------- | :---------------------------------- |
     *  | [ShopError](shoperror.html)              | No or invalid cartMetadata provided |
     *
     *  Note: Failed validation will NOT throw an error
     */
    productMetadata(cartProduct, mutateErrors) {
        if (!CartProduct.isCartProduct(cartProduct)) {
            throw slugError('Failed to validate product metadata -> invalid cart product', ['validation', 'productMetadata', 'error', 'product']);
        }
        return this._validateCartItemMetadata(cartProduct, mutateErrors);
    }
    /**
     *  Validate all (global) product cart metadata.
     *
     *  This will return true iff all metadata is valid.
     *  On failure, the errors are set on the cart metadata object
     *
     *  ```ts
     *  const validationResponse: ValidationResponse = cart.validator.allProductMetadata();
     *  ```
     *
     *  | Error                                    | Reason                              |
     *  | :--------------------------------------- | :---------------------------------- |
     *  | [ShopError](shoperror.html)              | No or invalid cartMetadata provided |
     *
     *  Note: Failed validation will NOT throw an error
     */
    allProductMetadata(mutateErrors) {
        return mergeValidation(this._cart.flatItems[CartItemChildType.Products], (cartProduct) => {
            if (cartProduct.childOf) {
                return validationResponseFromValidationErrors([]);
            }
            return this.productMetadata(cartProduct, mutateErrors);
        });
    }
    /**
     *  Validate all metadata (booker, ticket & product).
     *
     *  This will return true iff all metadata is valid.
     *  On failure, the errors are set on the cart metadata object
     *
     *  ```ts
     *  const validationResponse: ValidationResponse = cart.validator.allMetadata();
     *  ```
     *
     *  | Error                                    | Reason                              |
     *  | :--------------------------------------- | :---------------------------------- |
     *  | [ShopError](shoperror.html)              | No or invalid cartMetadata provided |
     *
     *  Note: Failed validation will NOT throw an error
     */
    allMetadata(mutateErrors) {
        return mergeValidation({
            booker: this.bookerMetadata(mutateErrors),
            ticket: this.allTicketMetadata(mutateErrors),
            product: this.allProductMetadata(mutateErrors),
        }, (item) => item);
    }
    /**
     *  Validate ticket constraints for a ticket
     *
     *  E.g.: min/max orderable amount of tickets
     *
     *  If the ticket is not ordered, it is NOT invalid,
     *  even if the min orderable amount is larger than zero.
     *
     *  ```ts
     *  const validationResponse: ValidationResponse = cart.validator.ticket(ticket);
     *  ```
     */
    ticket(ticket, mutateErrors = true) {
        // If the ticket is not ordered, it is NOT invalid,
        // even if the min orderable amount is larger than zero.
        if (!Object.prototype.hasOwnProperty.call(this._cart.items, ticket.event_id) ||
            !Object.prototype.hasOwnProperty.call(this._cart.items[ticket.event_id][CartItemChildType.Tickets].map, ticket.guid)) {
            return validationResponseFromValidationErrors([]);
        }
        // TODO Where should we get these defaults from... ?
        const min = ticket.min_orderable_amount_per_order || 1;
        const max = ticket.max_orderable_amount_per_order || 20;
        const increment = ticket.increment || 1;
        const cartTicketCount = Object.keys(this._cart.items[ticket.event_id][CartItemChildType.Tickets].map[ticket.guid]).length;
        const ticketErrors = [];
        if (cartTicketCount > 0) {
            // TODO Should we set a errors somewhere ??? (With mutateErrors check)
            if (cartTicketCount < min) {
                ticketErrors.push(`invalid minimum orderable amount per order: should contain at least ${ticket.min_orderable_amount_per_order} tickets`);
            }
            if (cartTicketCount > max) {
                ticketErrors.push(`invalid maximum orderable amount per order: should contain at most ${ticket.max_orderable_amount_per_order} tickets`);
            }
            if ((cartTicketCount % increment) !== 0) {
                ticketErrors.push(`invalid increment: should contain a strict multiple of ${ticket.increment} tickets`);
            }
        }
        return validationResponseFromValidationErrors(ticketErrors, ticketErrors[0]);
    }
    /**
     *  Validate ticket constraints for all tickets
     *
     *  E.g.: min/max orderable amount of tickets
     *
     *  We can safely check the cart tickets here,
     *  as we define cart tickets to be valid,
     *  even when they have not been reserved
     *  AND the min orderable amount is larger than zero.
     *
     *  ```ts
     *  const validationResponse: ValidationResponse = cart.validator.allTickets();
     *  ```
     */
    allTickets(mutateErrors) {
        return mergeValidation(this._cart.items, (eventItems) => {
            return mergeValidation(eventItems[CartItemChildType.Tickets].map, (cartTickets) => {
                const firstCartTicket = Object.values(cartTickets)[0];
                if (!firstCartTicket) {
                    return validationResponseFromValidationErrors([]);
                }
                return this.ticket(firstCartTicket.item, mutateErrors);
            });
        });
    }
    /**
     *  Validate (optional) product constraints for a product group of a cart ticket.
     *
     *  A product group is considered valid if the count of the ticket's products is within the bounds.
     *  And the uniqueness is not surpassed for each individual product type.
     *
     *  ```ts
     *  const validationResponse: ValidationResponse = cart.validator.ticketProductGroup(cartTicket, productGroup);
     *  ```
     */
    ticketProductGroup(cartTicket, productGroup, mutateErrors = true) {
        const productGroupErrors = [];
        const productErrors = {};
        let productMessage = '';
        let totalCartProductCount = 0;
        // TODO Should we set a errors somewhere ??? (With mutateErrors check)
        // Test the product group for every product type in the ticket.
        // The count of these types is used to validate the min and max bounds.
        // Inside the loop, the uniqueness is checked for every product type.
        forOwn(cartTicket.products.map, (cartProducts, productGuid) => {
            const groupProduct = productGroup.products.find((product) => product.guid === productGuid);
            // If the product is not found, this cart product set does not apply to this product group.
            if (!groupProduct) {
                return;
            }
            const cartProductCount = Object.keys(cartProducts).length;
            totalCartProductCount += cartProductCount;
            const uniqueness = groupProduct.uniqueness || productGroup.uniqueness;
            if (!!uniqueness && cartProductCount > uniqueness) {
                productErrors[productGuid] = ['product uniqueness invalid'];
                productMessage = 'product uniqueness invalid';
            }
        });
        if (totalCartProductCount < productGroup.min_bound) {
            productGroupErrors.push(`invalid min bound: should contain at least ${productGroup.min_bound} products`);
        }
        else if (!!productGroup.max_bound && totalCartProductCount > productGroup.max_bound) {
            productGroupErrors.push(`invalid max bound: should contain at most ${productGroup.max_bound} products`);
        }
        return mergeValidation({
            count: validationResponseFromValidationErrors(productGroupErrors, productGroupErrors[0]),
            products: validationResponseFromValidationErrors(productErrors, productMessage),
        }, (item) => item);
    }
    /**
     *  Validate (optional) product constraints for a cart ticket.
     *
     *  This is done using the constraints of product groups.
     *
     *  ```ts
     *  const validationResponse: ValidationResponse = cart.validator.ticketProducts(cartTicket);
     *  ```
     */
    ticketProducts(cartTicket, mutateErrors = true) {
        // Convert list to map
        const productGroups = cartTicket.item.groups.reduce((carry, item) => {
            carry[item.guid] = item;
            return carry;
        }, {});
        const groupErrors = mergeValidation(productGroups, (productGroup) => this.ticketProductGroup(cartTicket, productGroup, mutateErrors));
        // Also ensure all required products not part of a group are validated.
        // Note: This could result in a product flagged as an error both as required product and as invalid group
        const productErrors = {};
        let productMessage = '';
        forEach(cartTicket.item.products.required, (product) => {
            // TODO Should we set a errors somewhere ??? (With mutateErrors check)
            if (!Object.prototype.hasOwnProperty.call(cartTicket.products.map, product.guid)) {
                productErrors[product.guid] = ['product is required'];
                productMessage = 'product is required';
            }
        });
        return mergeValidation({
            groups: groupErrors,
            requiredProducts: validationResponseFromValidationErrors(productErrors, productMessage),
        }, (item) => item);
    }
    /**
     *  Validate (optional) product constraints for all cart tickets.
     *
     *  This is done using the constraints of product groups.
     *
     *  ```ts
     *  const validationResponse: ValidationResponse = cart.validator.allTicketProducts();
     *  ```
     */
    allTicketProducts(mutateErrors) {
        return mergeValidation(this._cart.flatItems[CartItemChildType.Tickets], this.ticketProducts.bind(this), mutateErrors);
    }
    /**
     *  Validate if the cart has items.
     *
     *  ```ts
     *  const validationResponse: ValidationResponse = cart.validator.hasItems();
     *  ```
     */
    hasItems() {
        let messages = [];
        if (!this._cart.hasItems()) {
            messages.push('invalid items: should contain at least 1 item');
        }
        return validationResponseFromValidationErrors(messages, messages[0]);
    }
    /**
     *  Validate all validators for the cart.
     *
     *  ```ts
     *  const validationResponse: ValidationResponse = cart.validator.all();
     *  ```
     */
    all() {
        const result = mergeValidation({
            // An order should have at least one item
            items: this.hasItems(),
            // Ticket constraints should be valid
            tickets: this.allTickets(),
            // Product group constraints should be valid
            ticket_products: this.allTicketProducts(),
            // All metadata validation should succeed
            metadata: this.allMetadata(),
            // All terms should be accepted
            terms: this.allTerms(),
            // The chosen payment method should be valid
            // Note: Most payment methods can ONLY be valid
            payment_methods: this.paymentMethod(true),
        }, (item) => item);
        if (!result.valid) {
            this._log._create(LogLevel.Debug, ['cart', 'validation', 'all', 'invalid'], result);
        }
        return result;
    }
    /**
     *  Validate if all terms are accepted.
     *
     *  This will return true iff all terms are accepted.
     *
     *  ```ts
     *  const validationResponse: ValidationResponse = cart.validator.allTerms();
     *  ```
     */
    allTerms(mutateErrors = true) {
        const termsErrors = {};
        let message = '';
        forOwn(this._cart.terms, (term, key) => {
            if (!term) {
                // TODO Should we set a errors somewhere ??? (With mutateErrors check)
                termsErrors[key] = ['term should be accepted']; // TODO Translate
                message = 'term should be accepted';
            }
        });
        return validationResponseFromValidationErrors(termsErrors, message);
    }
    /**
     *  Validate the custom properties for the chosen payment method.
     *
     *  Note: For the default payment method(s), this is currently irrelevant.
     *  The default payment methods are always valid.
     */
    paymentMethod(checkStripeToken = false, mutateErrors = true) {
        const result = this._paymentMethod(checkStripeToken, mutateErrors);
        if (!result.valid) {
            this._log._create(LogLevel.Debug, ['cart', 'validation', 'paymentMethod', 'invalid'], result);
        }
        return result;
    }
    _paymentMethod(checkStripeToken = false, mutateErrors = true) {
        const cartPaymentMethod = this._cart.paymentMethod;
        if (!cartPaymentMethod) {
            return validationResponseFromValidationErrors(['Payment method missing'], 'Payment method missing');
        }
        if (cartPaymentMethod.is_stripe && this._cart.checkout_details.total_price > 0) {
            if (!cartPaymentMethod.item.stripe || !cartPaymentMethod.item.stripe.complete) {
                return validationResponseFromValidationErrors(['Stripe should be completed'], 'Stripe should be completed');
            }
            if (cartPaymentMethod.item.stripe.errors.length) {
                const errors = cartPaymentMethod.item.stripe.errors.map((error) => error.message);
                return validationResponseFromValidationErrors(errors, errors[0]);
            }
            if (checkStripeToken && !cartPaymentMethod.stripe_token) {
                return validationResponseFromValidationErrors(['Stripe token missing'], 'Stripe token missing');
            }
        }
        return validationResponseFromValidationErrors([]);
    }
    /**
     *  Validate the cart metadata collection of a given cart item.
     *
     *  This will return true iff all metadata is valid.
     *  On failure, the errors are set on the cart metadata object
     *
     *  ```ts
     *  const validationOk: boolean = cart._validateCartItemMetadata(cartItem);
     *  ```
     *
     *  Note: this method expects its first argument to be of type CartItem.
     *  A CartItem type is a generic fixed to a Reservable type. By using `Reservable<any>`
     *  here, the exact type CartItem implementation can be extended in one (or at least less)
     *  place(s).
     *
     *  | Error                                    | Reason                              |
     *  | :--------------------------------------- | :---------------------------------- |
     *  | [ShopError](shoperror.html)              | No or invalid cartMetadata provided |
     *
     *  Note: Failed validation will NOT throw an error
     *
     *  @internal
     */
    _validateCartItemMetadata(cartItem, mutateErrors = true) {
        const metadataErrors = mergeValidation(cartItem.metadata, this.metadata.bind(this), mutateErrors);
        const childErrors = mergeValidation(CartItemChildType, (childType) => {
            if (!Object.prototype.hasOwnProperty.call(cartItem.children, childType) || !cartItem.children[childType]) {
                return validationResponseFromValidationErrors({});
            }
            return mergeValidation(cartItem.children[childType].map, (childItems) => {
                return mergeValidation(childItems, this._validateCartItemMetadata.bind(this), mutateErrors);
            });
        });
        return mergeValidation({
            metadata: metadataErrors,
            children: childErrors,
        }, (item) => item);
    }
}
/**
 *  Helper method to compile and merge validation errors and state.
 *
 *  The info needed for this is gathered by running the same callback on all items in the input.
 */
function mergeValidation(items, cb, mutateErrors) {
    const errors = {};
    let message = '';
    forOwn(items, (item, key) => {
        // Explicitly do not cancel the loop before all metadata are handled!
        // Even if one fails, we still want to know the errors of the rest.
        const validationResponse = cb(item, mutateErrors);
        if (!validationResponse.valid) {
            errors[key] = validationResponse.errors;
            message = message || validationResponse.message;
        }
    });
    return validationResponseFromValidationErrors(errors, message);
}
/**
 *  Helper method to created a validation response from validation errors.
 *
 *  The validation response is defined to be valid if no errors are given.
 */
function validationResponseFromValidationErrors(errors, message) {
    return {
        valid: !Object.keys(errors).length,
        message: message || '',
        errors,
    };
}
//# sourceMappingURL=validation.js.map